import React, { useState, useEffect } from 'react';

import { Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress, TextField, MenuItem, Box, Grid, Typography, Divider, Paper } from '@mui/material';
import { toast } from 'sonner';
import { useAuthStore } from '../../../store/useAuthStore';
import apiClient from '../../../services/api-client';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MoneyIcon from '@mui/icons-material/Money';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ApartmentIcon from '@mui/icons-material/Apartment';
import { SmartphoneIcon } from 'lucide-react';

const methodConfig = {
    'ESPECES': { label: 'Espèces', icon: <MoneyIcon sx={{ fontSize: 18 }} />, color: '#2e7d32' },
    'CARTE_BANCAIRE': { label: 'Carte', icon: <CreditCardIcon sx={{ fontSize: 18 }} />, color: '#1565c0' },
    'VIREMENT': { label: 'Virement', icon: <AccountBalanceIcon sx={{ fontSize: 18 }} />, color: '#6a1b9a' },
    'CHEQUE': { label: 'Chèque', icon: <ApartmentIcon sx={{ fontSize: 18 }} />, color: '#ef6c00' },
    'MOBILE_MONEY': { label: 'Mobile', icon: <SmartphoneIcon sx={{ fontSize: 18 }} />, color: '#388e3c' },
};

export const AddPaymentDialog = ({ invoice, open, onOpenChange, onSuccess }) => {
    const [amount, setAmount] = useState('');
    const [method, setMethod] = useState('ESPECES');
    const [reference, setReference] = useState('');
    const [notes, setNotes] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const user = useAuthStore((s) => s.user);
    const remaining = invoice
        ? Math.max(0, (invoice.montantTotal || 0) - (invoice.montantPaye || 0))
        : 0;
    const [details, setDetails] = useState(null);
    const [loading, setLoading] = useState(true);


    useEffect(() => {
        const fetchPayments = async () => {
            try {
                const res = await apiClient.get(`/api/invoices/${invoice.id}/details`);
                setDetails(res.data);
            } catch (error) {
                console.error('Error fetching payments:', error);
                setDetails(null);
            } finally {
                setLoading(false);
            }
        };

        fetchPayments();
    }, [invoice]);

    useEffect(() => {
        if (open) {
            setAmount('');
            setMethod('ESPECES');
            setReference('');
            setNotes('');
        }
    }, [open]);

    const handleSubmit = async () => {
        // Vérifications de base
        if (!invoice || !amount || Number(amount) <= 0) return;

        if (Number(amount) > remaining) {
            toast.error(`Le montant ne peut pas dépasser le reste à payer (${remaining.toLocaleString('fr-FR')} €)`);
            return;
        }

        setIsSubmitting(true);

        try {
            // Utilisation de apiClient (Axios) au lieu de fetch
            const response = await apiClient.post(`/api/paiements/facture/${invoice.id}`, {
                typeOperation: 'ENCAISSEMENT',
                montant: Number(amount),
                modePaiement: method,
                referenceTransaction: reference || undefined,
                commentaire: notes || undefined,
                codePaiementOrigine: null,
            });

            // Avec Axios, si on arrive ici, c'est que le statut est 2xx
            toast.success(`Versement de ${Number(amount).toLocaleString('fr-FR')} € enregistré avec succès`);
            onOpenChange(false);
            onSuccess();

        } catch (err) {
            // Gestion des erreurs Axios
            console.error("Erreur lors de l'enregistrement du paiement:", err);

            const errorMessage = err.response?.data?.message
                || err.response?.data?.error
                || "Erreur lors de l'enregistrement";

            toast.error(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const quickAmounts = invoice
        ? [
            { label: '25%', value: (remaining * 0.25).toFixed(2) },
            { label: '33%', value: (remaining / 3).toFixed(2) },
            { label: '50%', value: (remaining * 0.5).toFixed(2) },
            { label: '75%', value: (remaining * 0.75).toFixed(2) },
            { label: 'Solde', value: remaining.toFixed(2) },
        ]
        : [];

    return (
        <Dialog
            open={open}
            onClose={() => onOpenChange(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: '#c9a84c',
                    color: '#1a1a2e',
                    borderRadius: 3
                }
            }}
        >
            <Box sx={{ p: 2, backgroundColor: 'rgba(201, 168, 76, 0.21)' }}>
                <DialogTitle sx={{ color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CreditCardIcon sx={{ color: '#c9a84c', fontSize: 22 }} />
                    Enregistrer un paiement
                </DialogTitle>

                <DialogContent>
                    {invoice && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
                            <Paper elevation={0} sx={{ border: '1px solid rgba(201, 168, 76, 0.12)', borderRadius: 2, p: 2 }}>
                                {[
                                    { label: 'Facture', value: details?.numeroFacture, valueStyle: { color: '#c9a84c', fontWeight: 600 } },
                                    { label: 'Client', value: details?.clientInfo?.nomComplet, valueStyle: {} },
                                    { label: 'Dossier', value: details?.dossierInfo?.referenceCode, valueStyle: {} },
                                ].map(({ label, value, valueStyle }) => (
                                    <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                                        <Typography variant="caption" sx={{ color: '#888' }}>{label}</Typography>
                                        <Typography variant="caption" sx={{ color: '#1a1a2e', ...valueStyle }}>{value}</Typography>
                                    </Box>
                                ))}
                                <Divider sx={{ my: 1, borderColor: 'rgba(201, 168, 76, 0.08)' }} />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                                    <Typography variant="caption" sx={{ color: '#888' }}>Total TTC</Typography>
                                    <Typography variant="caption" sx={{ color: '#1a1a2e', fontWeight: 600 }}>{invoice.montantTotal.toLocaleString('fr-FR')} <span className='text-xs text-gray-500'>FCFA</span></Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                                    <Typography variant="caption" sx={{ color: '#888' }}>Total payé</Typography>
                                    <Typography variant="caption" sx={{ color: '#1a1a2e', fontWeight: 600 }}>{invoice.montantPaye.toLocaleString('fr-FR')} <span className='text-xs text-gray-500'>FCFA</span></Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="caption" sx={{ color: '#888' }}>Reste à payer</Typography>
                                    <Typography variant="caption" sx={{ color: '#c62828', fontWeight: 600 }}>{remaining.toLocaleString('fr-FR')} <span className='text-xs text-gray-500'>FCFA</span></Typography>
                                </Box>
                            </Paper>

                            <Box>
                                <Typography variant="body2" sx={{ color: '#1a1a2e', mb: 1, fontFamily: "'DM Sans', sans-serif" }}>
                                    Montant du versement
                                </Typography>
                                <TextField
                                    type="number"
                                    fullWidth
                                    placeholder="0.00"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                    inputProps={{ min: 0.01, max: remaining, step: 0.01 }}
                                    InputProps={{
                                        endAdornment: <Typography sx={{ color: '#888', mr: 1 }}>€</Typography>,
                                        sx: { fontSize: 18, fontWeight: 600, backgroundColor: 'white', borderRadius: 0.5 },
                                    }}
                                    sx={{ backgroundColor: 'white', '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'rgba(201, 168, 76, 0.2)' } } }}
                                />
                                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                    {quickAmounts.map((qa) => (
                                        <Button
                                            key={qa.label}
                                            variant="outlined"
                                            size="small"
                                            sx={{
                                                flex: 1,
                                                fontSize: 11,
                                                height: 28,
                                                borderColor: 'rgba(201, 168, 76, 0.2)',
                                                color: '#666',
                                                textTransform: 'none',
                                                borderRadius: 0.5,
                                                '&:hover': { borderColor: '#c9a84c', color: '#c9a84c', backgroundColor: 'rgba(201,168,76,0.05)' },
                                            }}
                                            onClick={() => setAmount(qa.value)}
                                        >
                                            {qa.label}
                                        </Button>
                                    ))}
                                </Box>
                            </Box>

                            {/* Payment Method */}
                            <Box>
                                <Typography variant="body2" sx={{ color: '#1a1a2e', mb: 1, fontFamily: "'DM Sans', sans-serif" }}>
                                    Mode de paiement
                                </Typography>
                                <Grid container spacing={1}>
                                    {Object.entries(methodConfig).map(([key, config]) => (
                                        <Grid item xs={6} key={key}>
                                            <Box
                                                onClick={() => setMethod(key)}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    px: 1,
                                                    py: 1,
                                                    textTransform: 'uppercase',
                                                    borderRadius: 0.5,
                                                    border: `1px solid ${method === key ? config.color : 'rgba(201, 168, 76, 0.12)'}`,
                                                    backgroundColor: method === key ? `${config.color}08` : 'white',
                                                    color: method === key ? config.color : '#666',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.15s',
                                                    '&:hover': { borderColor: config.color, backgroundColor: `${config.color}05` },
                                                }}
                                            >
                                                {config.icon}
                                                <Typography variant="caption" sx={{ fontWeight: 500, fontSize: 10 }}>
                                                    {config.label}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>

                            {/* Reference */}
                            {method !== "ESPECES" && (
                                <TextField
                                    fullWidth
                                    label="Référence"
                                    placeholder="VIR-2024-XXXX, CHQ-XXXX..."
                                    value={reference}
                                    onChange={(e) => setReference(e.target.value)}
                                    size="small"
                                    sx={{
                                        borderRadius: 0.5,
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: 'white',
                                            '& fieldset': { borderColor: 'rgba(201, 168, 76, 0.2)' },
                                        },
                                    }}
                                />
                            )}
                            {/* Notes */}
                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                label="Commentaire (optionnel)"
                                placeholder="ex: 1er versement échelonné, acompte..."
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                size="small"
                                sx={{
                                    borderRadius: 0.5,
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: 'white',
                                        '& fieldset': { borderColor: 'rgba(201, 168, 76, 0.2)' },
                                    },
                                }}
                            />
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                    <Button
                        onClick={() => onOpenChange(false)}
                        variant="outlined"
                        sx={{ borderColor: 'rgba(201, 168, 76, 0.2)', color: '#666', textTransform: 'none', borderRadius: 2 }}
                    >
                        Annuler
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={!amount || Number(amount) <= 0 || isSubmitting}
                        startIcon={isSubmitting ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <CheckCircleIcon />}
                        sx={{
                            background: 'linear-gradient(135deg, #b08d3a, #c9a84c)',
                            color: 'white',
                            textTransform: 'none',
                            borderRadius: 0.5,
                            fontFamily: "'DM Sans', sans-serif",
                            '&:hover': { background: 'linear-gradient(135deg, #9a7a30, #b8983e)' },
                            '&.Mui-disabled': { background: 'rgba(0,0,0,0.12)', color: 'rgba(0,0,0,0.26)' },
                        }}
                    >
                        {isSubmitting ? 'Enregistrement...' : 'Enregistrer le paiement'}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
}